
/**
 * Pisada Serena - Compra Tranquila Plus
 *
 * Variant: 55345775247703
 * Default: automatically added
 *
 * - Automatically adds Plus to non-empty carts
 * - Customer can opt out
 * - Remembers opt-out during browser session
 * - Synchronizes checkbox with actual Shopify cart
 * - Detects Shopify Ajax cart changes
 * - Prevents duplicate additions
 * - Refreshes cart drawer after Plus changes
 */

(() => {
  if (window.PSCompraTranquilaInitialized) return;
  window.PSCompraTranquilaInitialized = true;

  const VARIANT_ID = 55345775247703;
  const ROOT = window.Shopify?.routes?.root || '/';
  const OPT_OUT_KEY = 'ps_compra_tranquila_plus_opt_out';

  let busy = false;
  let scheduled = false;
  let pendingCheck = false;
  let internalCartUpdate = false;

  const cartUrl = (path) => ROOT + path;

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function request(path, options = {}) {
    const response = await fetch(cartUrl(path), {
      credentials: 'same-origin',
      ...options
    });

    if (!response.ok) {
      throw new Error(`Cart API error: ${response.status}`);
    }

    return response.json();
  }

  async function getCart() {
    return request('cart.js');
  }

  function getPlusItems(cart) {
    return cart.items.filter(
      (item) => Number(item.variant_id) === VARIANT_ID
    );
  }

  function hasOtherProducts(cart) {
    return cart.items.some(
      (item) =>
        Number(item.variant_id) !== VARIANT_ID &&
        item.quantity > 0
    );
  }

  function hasOptedOut() {
    try {
      return sessionStorage.getItem(OPT_OUT_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function setOptOut(value) {
    try {
      if (value) {
        sessionStorage.setItem(OPT_OUT_KEY, '1');
      } else {
        sessionStorage.removeItem(OPT_OUT_KEY);
      }
    } catch (_) {
      // Storage unavailable.
    }
  }

  function setCheckboxes(checked, disabled = false) {
    document.querySelectorAll(
      '[data-ps-plus-checkbox]'
    ).forEach((checkbox) => {
      checkbox.checked = checked;
      checkbox.disabled = disabled;
    });
  }

  function showError(visible) {
    document.querySelectorAll(
      '[data-ps-plus-error]'
    ).forEach((element) => {
      element.hidden = !visible;
    });
  }

  /**
   * Refresh drawer content and summary.
   */
  async function refreshCartUI() {
    const response = await fetch(
      window.location.pathname,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Unable to refresh cart drawer');
    }

    const html = await response.text();

    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const currentDrawer = document.querySelector(
      'cart-drawer-component'
    );

    const freshDrawer = parsed.querySelector(
      'cart-drawer-component'
    );

    if (!currentDrawer || !freshDrawer) {
      throw new Error('Cart drawer markup not found');
    }

    for (const selector of [
      '.cart-drawer__content',
      '.cart-drawer__summary'
    ]) {
      const current = currentDrawer.querySelector(selector);
      const fresh = freshDrawer.querySelector(selector);

      if (current && fresh) {
        current.replaceWith(
          document.importNode(fresh, true)
        );
      }
    }
  }

  /**
   * Synchronize checkbox with actual cart.
   */
  async function syncCheckboxes() {
    try {
      const cart = await getCart();

      setCheckboxes(
        getPlusItems(cart).length > 0,
        busy
      );

      return cart;
    } catch (error) {
      console.error(
        '[Compra Tranquila Plus] Sync error:',
        error
      );

      return null;
    }
  }

  /**
   * Add or remove Plus.
   */
  async function updatePlus(shouldAdd) {
    if (busy) return;

    busy = true;
    internalCartUpdate = true;

    setCheckboxes(shouldAdd, true);
    showError(false);

    try {
      const cart = await getCart();
      const plusItems = getPlusItems(cart);

      if (shouldAdd) {
        if (
          !plusItems.length &&
          hasOtherProducts(cart)
        ) {
          await request('cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: VARIANT_ID,
                quantity: 1
              }]
            })
          });
        }
      } else {
        for (const item of plusItems) {
          await request('cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        }
      }

      await refreshCartUI();
      await syncCheckboxes();

    } catch (error) {
      console.error(
        '[Compra Tranquila Plus] Update error:',
        error
      );

      showError(true);

      await syncCheckboxes();

    } finally {
      busy = false;
      internalCartUpdate = false;

      document.querySelectorAll(
        '[data-ps-plus-checkbox]'
      ).forEach((checkbox) => {
        checkbox.disabled = false;
      });

      if (pendingCheck) {
        pendingCheck = false;
        scheduleDefaultCheck(200);
      }
    }
  }

  /**
   * Automatically ensure Plus is included.
   */
  async function ensurePlusDefault() {
    if (busy) {
      pendingCheck = true;
      return;
    }

    try {
      const cart = await getCart();

      // Empty cart: reset customer preference.
      if (cart.items.length === 0) {
        setOptOut(false);
        setCheckboxes(false);
        return;
      }

      // No regular products.
      if (!hasOtherProducts(cart)) {
        setCheckboxes(
          getPlusItems(cart).length > 0
        );
        return;
      }

      // Plus already included.
      if (getPlusItems(cart).length > 0) {
        setCheckboxes(true);
        return;
      }

      // Customer explicitly opted out.
      if (hasOptedOut()) {
        setCheckboxes(false);
        return;
      }

      // Automatically add Plus by default.
      // The customer can still opt out manually.
      await updatePlus(true);

    } catch (error) {
      console.error(
        '[Compra Tranquila Plus] Auto-add error:',
        error
      );
    }
  }

  /**
   * Schedule cart check after Shopify updates.
   */
  function scheduleDefaultCheck(delay = 150) {
    if (busy) {
      pendingCheck = true;
      return;
    }

    if (scheduled) return;

    scheduled = true;

    wait(delay).then(async () => {
      scheduled = false;
      await ensurePlusDefault();
    });
  }

  /**
   * Checkbox interaction.
   */
  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest?.(
      '[data-ps-plus-checkbox]'
    );

    if (!checkbox || busy) return;

    const shouldAdd = checkbox.checked;

    setOptOut(!shouldAdd);

    updatePlus(shouldAdd);
  });

  /**
   * Prevent checkout while updating Plus.
   */
  document.addEventListener(
    'click',
    (event) => {
      const checkout = event.target.closest?.(
        'cart-drawer-component [name="checkout"]'
      );

      if (checkout && busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /**
   * Detect Shopify Ajax cart mutations.
   *
   * Only observes completed cart mutation requests.
   * Does not intercept or modify their responses.
   */
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);

    try {
      const input = args[0];

      const url = typeof input === 'string'
        ? input
        : input?.url || '';

      const parsedUrl = new URL(
        url,
        window.location.origin
      );

      const isCartMutation =
        /\/cart\/(add|change|update|clear)\.js$/.test(
          parsedUrl.pathname
        );

      if (isCartMutation && !internalCartUpdate) {
        result.then((response) => {
          if (response.ok) {
            scheduleDefaultCheck(350);
          }
        }).catch(() => {});
      }
    } catch (_) {
      // Ignore non-cart requests.
    }

    return result;
  };

  /**
   * Detect cart updates made via XMLHttpRequest.
   */
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...rest
  ) {
    try {
      const parsedUrl = new URL(
        url,
        window.location.origin
      );

      this._psPlusCartMutation =
        /\/cart\/(add|change|update|clear)\.js$/.test(
          parsedUrl.pathname
        );
    } catch (_) {
      this._psPlusCartMutation = false;
    }

    return originalXHROpen.call(
      this,
      method,
      url,
      ...rest
    );
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (this._psPlusCartMutation) {
      this.addEventListener(
        'loadend',
        () => {
          if (
            this.status >= 200 &&
            this.status < 300 &&
            !internalCartUpdate
          ) {
            scheduleDefaultCheck(350);
          }
        },
        { once: true }
      );
    }

    return originalXHRSend.apply(this, args);
  };

  /**
   * Theme cart events.
   */
  [
    'cart:updated',
    'cart:change',
    'cart:refresh'
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      scheduleDefaultCheck(250);
    });
  });

  /**
   * Recheck when interacting with drawer.
   */
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-ps-plus]')) {
      return;
    }

    if (
      document.querySelector('cart-drawer-component')
    ) {
      scheduleDefaultCheck(350);
    }
  });

  /**
   * Recheck when returning to the page.
   */
  window.addEventListener('pageshow', () => {
    scheduleDefaultCheck(200);
  });

  /**
   * Initial load.
   */
  function initialize() {
    scheduleDefaultCheck(100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

})();
